import type { ServerIssue } from "./form-errors";
import type {
  ActiveMedicalCaseResponse,
  CareMeetingRequirementView,
  ClinicianAssessmentPhotosView,
  ReviewAssessmentPhotosInput,
  CloseCheckinWithoutReplyRequest,
  DoseLogView,
  DoseScheduleView,
  OlderSymptomRegistrationsView,
  StaffPatientSymptomsView,
  OverviewCompletionRequest,
  OverviewCompletionRequestResult,
  SetDoseScheduleAnchorRequest,
  SetTransferTreatmentStartRequest,
  TransferTreatmentStartView,
  MedicalCaseResponse,
  RecordCareRelationshipEnding,
  MeetingSummaryView,
  PublishMeetingSummaryRequest,
  SaveMeetingSummaryRequest,
} from "@care-by-ella/contracts";
import type {
  AiAssistCoachReply,
  AiAssistFormOverview,
  AiAssistUsedField,
  CareCaseTransitionResult,
  CheckinCoachActionResult,
  CheckinCoachHandlingDetail,
  CompletionHints,
  CompletionNote,
  CompletionNoteFinalized,
  JournalWebdocStatus,
  LabWebdocAssessment,
  LabWebdocAssessmentResult,
  UpdateCompletionNote,
  DoctorNoticeRequest,
  DoctorNoticeResponse,
  DoctorQuestionHandledResponse,
  DoctorQuestionList,
  DoctorQuestionMedicalAction,
  DoctorQuestionWithdrawnResponse,
  MedicalQuestionAnswerRequest,
  MedicalQuestionAnswerResponse,
  MedicalReviewCaseDetail,
  MedicalReviewCaseSummary,
  PatientFormFacts,
  PublishTreatmentDecision,
  PublishTreatmentDecisionResult,
  PublishTreatmentStatusDecision,
  PublishTreatmentStatusDecisionResult,
  SendCheckinCoachReplyRequest,
  SubscriptionPlan,
  SubscriptionLinkPreview,
  TransitionCareCaseRequest,
} from "@care-by-ella/contracts";
import type {
  ClinicianMedicalEvidence,
  EvidenceSummary,
  CorrectMeasurementSession,
  CorrectMedicalProfile,
  Measurement,
  MedicalProfileEntryDto,
  MedicalProfileResponse,
  MeetingLiveStatus,
  MeetingRatingStats,
  TreatmentDecisionNote,
  TreatmentDecisionNoteDraft,
  TreatmentDecisionNoteFinalized,
} from "@care-by-ella/contracts";
import {
  patientListQueryString,
  type PatientListParams,
} from "./patient-list-page";
import type {
  ChatMessage,
  CoachPatientDetail,
  CoachPatientList,
  ChatMessageKind,
  ChatSenderType,
  HomeNowResponse,
  ClinicianLabFeedback,
  ClinicianLabFeedbackApprovalInput,
  ClinicianLabFeedbackInput,
  ClinicianMeeting,
  ClinicianMeetingCalendar,
  DocumentMeetingAndDecideRequest,
  DocumentMeetingRequest,
  Flag,
  FormSubmission,
  HardStop,
  InboxRow,
  MeetingDocumentation,
  MeetingJournal,
  JournalSectionAiRequest,
  JournalSectionAiResult,
  MeetingPrep,
  UpdateMeetingJournal,
  PatientDetail,
  PatientList,
  PatientListRow,
  PatientPhotoList,
  PatientPhotoMeta,
  WeekSchedule,
  WeekScheduleUpdate,
  ScheduleDay,
  ScheduleDayBlock,
  SchedulePatternApply,
  SchedulePatternResult,
  ScheduleSettings,
  ScheduleSettingsUpdate,
  WellbeingSeriesView,
} from "@care-by-ella/contracts";
import type {
  CoachCycleView,
  CoachMemory,
  CoachMemoryItem,
  CreateCoachMemoryItemRequest,
  DoctorCycleView,
  F01StartContext,
  RetireCoachMemoryItemRequest,
  ReviseCoachMemoryItemRequest,
} from "@care-by-ella/contracts";
import { API_BASE, apiFetch, tenantHeader, withCredentials } from "./api";
import { forbiddenText } from "./error-text";

// CAR-36: every clinician request now authenticates via the httpOnly session
// cookie. No JWT is passed in JS — `withCredentials` makes the browser attach
// the cookie, and the API resolves the clinician from it. State-changing
// requests also pass the browser's `Origin` header, which the API's CSRF
// guard checks.
//
// CAR-67: DTOs that cross the network are owned by
// `@care-by-ella/contracts` — Flag, HardStop, FormSubmission are imported
// from there and re-exported below so the existing call sites that
// import them from this module keep working.

export type {
  ChatMessage,
  ChatMessageKind,
  ChatSenderType,
  ClinicianLabFeedback,
  ClinicianLabFeedbackApprovalInput,
  ClinicianLabFeedbackInput,
  ClinicianMeeting,
  DocumentMeetingRequest,
  Flag,
  FormSubmission,
  HardStop,
  InboxRow,
  MeetingDocumentation,
  MeetingPrep,
  PatientDetail,
  PatientList,
  PatientListRow,
  PatientPhotoList,
  PatientPhotoMeta,
  WeekSchedule,
  WeekScheduleUpdate,
  ScheduleDay,
  ScheduleDayBlock,
};

// Common headers for every clinician request. The session cookie carries auth
// (see `withCredentials` on each fetch); only the tenant + content-type are
// set explicitly here.
export function commonHeaders(): Record<string, string> {
  return {
    "Content-Type": "application/json",
    ...tenantHeader(),
  };
}

// 401 — the session is gone/expired; the only sane response is signOut().
export class UnauthorizedError extends Error {}

// TES-363 — 403 is NOT a broken session: it means "your (valid) role may not
// do this". It used to map to UnauthorizedError, so a coach tapping a
// doctor-only link was signed out mid-shift. Routes let this fall through to
// their inline error state; the message is the user-facing copy.
//
// TES-811 — the API always said WHY it refused (`not_your_meeting`,
// `not_authorized`, …) and we threw that away, so a doctor opening a
// colleague's meeting read "Du saknar behörighet för den här sidan." and
// reported a permission bug. `code` keeps the machine cause for callers that
// branch on it; the message is that cause in Swedish when we have words for
// it, and the same generic line as before when we do not — so the surfaces
// that merely hide on 403 are unaffected.
export class ForbiddenError extends Error {
  readonly code: string | null;
  constructor(code: string | null = null) {
    super(forbiddenText(code));
    this.code = code;
  }
}

// CAR-62: the structured `{error, messageKey}` body the API returns when it
// refuses, so callers can surface the real cause instead of an opaque
// "HTTP 500". Null when the body isn't JSON-shaped (proxies, gateways) or
// carries no code.
async function errorCode(res: Response): Promise<string | null> {
  try {
    const body = (await res.json()) as { error?: string };
    return body?.error ?? null;
  } catch {
    return null; // body wasn't JSON
  }
}

export async function jsonOr401<T>(res: Response): Promise<T> {
  if (res.status === 401) {
    throw new UnauthorizedError(`HTTP ${res.status}`);
  }
  if (res.status === 403) {
    throw new ForbiddenError(await errorCode(res));
  }
  if (res.status === 400) {
    throw await badRequestError(res);
  }
  if (!res.ok) {
    throw new Error((await errorCode(res)) ?? `HTTP ${res.status}`);
  }
  return (await res.json()) as T;
}

// TES-1031 — a 400 that names the refused fields. The message stays the error
// code, exactly as before, so every caller that branches on `e.message` is
// unaffected; forms that can mark fields read `issues`.
export class ValidationError extends Error {
  readonly issues: ServerIssue[];
  constructor(code: string, issues: ServerIssue[]) {
    super(code);
    this.issues = issues;
  }
}

async function badRequestError(res: Response): Promise<Error> {
  try {
    const body = (await res.json()) as {
      error?: string;
      issues?: unknown;
      messageKey?: string;
      messageParams?: Record<string, unknown>;
    };
    const code = body?.error ?? `HTTP ${res.status}`;
    const issues = Array.isArray(body?.issues)
      ? body.issues.filter(
          (i): i is ServerIssue =>
            typeof i === "object" &&
            i !== null &&
            typeof (i as ServerIssue).path === "string" &&
            typeof (i as ServerIssue).message === "string",
        )
      : [];
    if (issues.length) return new ValidationError(code, issues);
    // TES-1522 — a refusal that names a value (the treatment start a dose
    // date may not precede) carries it for the sentence that shows it.
    if (body?.messageParams && typeof body.messageParams === "object") {
      return Object.assign(new Error(code), {
        messageKey: body.messageKey,
        messageParams: body.messageParams,
      });
    }
    return new Error(code);
  } catch {
    return new Error(`HTTP ${res.status}`);
  }
}

export async function fetchInbox(
  opts: { includeApproved?: boolean } = {},
): Promise<{ rows: InboxRow[] }> {
  // TES-487 — approved prescription transfers have left the queue; the toggle asks
  // for them explicitly.
  const qs = opts.includeApproved ? "?includeApproved=1" : "";
  const res = await apiFetch(`${API_BASE}/employee-api/v1/inbox${qs}`, {
    headers: commonHeaders(),
    ...withCredentials,
  });
  return jsonOr401(res);
}

// CAR-13 — searchable clinic patient list. `q` is an optional name or
// personal-number fragment. TES-1308 — one page at a time.
export async function fetchPatients(
  params?: string | PatientListParams,
): Promise<PatientList> {
  const qs = patientListQueryString(params);
  const res = await apiFetch(`${API_BASE}/employee-api/v1/patients${qs}`, {
    headers: commonHeaders(),
    ...withCredentials,
  });
  return jsonOr401(res);
}

// TES-756 — ONE week's availability, dated. `employeeId` omitted = the
// signed-in user's own schedule; given = someone else's, which the API gates
// on @Roles("admin").
function scheduleUrl(weekStart: string, employeeId?: string): string {
  const base = employeeId
    ? `${API_BASE}/employee-api/v1/clinicians/${encodeURIComponent(employeeId)}/schedule`
    : `${API_BASE}/employee-api/v1/me/schedule`;
  return `${base}?weekStart=${encodeURIComponent(weekStart)}`;
}

export async function fetchWeekSchedule(
  weekStart: string,
  employeeId?: string,
): Promise<WeekSchedule> {
  const res = await apiFetch(scheduleUrl(weekStart, employeeId), {
    headers: commonHeaders(),
    ...withCredentials,
  });
  return jsonOr401(res);
}

export async function saveWeekSchedule(
  update: WeekScheduleUpdate,
  employeeId?: string,
): Promise<WeekSchedule> {
  const res = await apiFetch(scheduleUrl(update.weekStart, employeeId), {
    method: "PUT",
    headers: commonHeaders(),
    body: JSON.stringify(update),
    ...withCredentials,
  });
  return jsonOr401(res);
}

// TES-1238 — a weekly pattern over a date range. Dates that already have
// other times come back as `conflicts` unless `overwrite` is set.
export async function applySchedulePattern(
  input: SchedulePatternApply,
  employeeId?: string,
): Promise<SchedulePatternResult> {
  const base = employeeId
    ? `${API_BASE}/employee-api/v1/clinicians/${encodeURIComponent(employeeId)}/schedule`
    : `${API_BASE}/employee-api/v1/me/schedule`;
  const res = await apiFetch(`${base}/pattern`, {
    method: "POST",
    headers: commonHeaders(),
    body: JSON.stringify(input),
    ...withCredentials,
  });
  return jsonOr401(res);
}

// TES-1238 — the doctor's per-hour meeting cap (admin only).
// TES-1261 — and the time between bookable start times.
export async function saveScheduleSettings(
  employeeId: string,
  settings: ScheduleSettingsUpdate,
): Promise<ScheduleSettings> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/clinicians/${encodeURIComponent(employeeId)}/schedule/settings`,
    {
      method: "PUT",
      headers: commonHeaders(),
      body: JSON.stringify(settings),
      ...withCredentials,
    },
  );
  return jsonOr401(res);
}

// TES-363 — the resolved "Hem just nu" (§2) preview: what the REAL HomeNow
// ladder returns for this patient right now (winning candidate + fallback).
export async function fetchHomeNowPreview(
  patientId: string,
  locale?: "sv" | "en",
): Promise<HomeNowResponse> {
  const qs = locale ? `?locale=${locale}` : "";
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/home-now-preview${qs}`,
    { headers: commonHeaders(), ...withCredentials },
  );
  return jsonOr401(res);
}

export async function fetchPatient(patientId: string): Promise<PatientDetail> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}`,
    {
      headers: commonHeaders(),
      ...withCredentials,
    },
  );
  return jsonOr401(res);
}

// TES-493 — the coach's read of a patient. A different endpoint, not a query
// flag on the one above: the doctor's payload carries doses, lab results and
// raw form answers, and the surest way to keep those out of a coach's browser
// is for the request that would return them never to be made.
// TES-1209 — the coach's "Patienter": the same roster and search as
// fetchPatients, with open cases, latest contact and latest check-in.
export async function fetchCoachPatients(
  params?: string | PatientListParams,
): Promise<CoachPatientList> {
  const qs = patientListQueryString(params);
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/coach-patients${qs}`,
    {
      headers: commonHeaders(),
      ...withCredentials,
    },
  );
  return jsonOr401(res);
}

export async function fetchCoachPatient(
  patientId: string,
): Promise<CoachPatientDetail> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/coach-view`,
    { headers: commonHeaders(), ...withCredentials },
  );
  return jsonOr401(res);
}

// TES-381 — patient body photos. The list is metadata-only; the bytes come
// from the per-photo endpoint via an authenticated blob fetch (the session
// cookie can't ride a bare <img src>, and the response needs the stored
// Content-Type) — the caller turns the Blob into an object URL to render it.
export async function fetchPatientPhotos(
  patientId: string,
): Promise<PatientPhotoList> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/photos`,
    { headers: commonHeaders(), ...withCredentials },
  );
  return jsonOr401(res);
}

export async function fetchPatientPhotoBlob(
  patientId: string,
  photoId: string,
): Promise<Blob> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/photos/${photoId}`,
    { headers: tenantHeader(), ...withCredentials },
  );
  if (res.status === 401) throw new UnauthorizedError(`HTTP ${res.status}`);
  if (res.status === 403) throw new ForbiddenError();
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.blob();
}

// TES-457 — the documents a transfer patient attached to F-07. Same shape as
// the photo bytes: authenticated blob fetch, stored Content-Type, object URL
// on the caller's side.
export async function fetchPatientUploadBlob(
  patientId: string,
  uploadId: string,
): Promise<Blob> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/uploads/${uploadId}`,
    { headers: tenantHeader(), ...withCredentials },
  );
  if (res.status === 401) throw new UnauthorizedError(`HTTP ${res.status}`);
  if (res.status === 403) throw new ForbiddenError();
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.blob();
}

// TES-734 — the queue "Receptövertag att granska" (doctor-only).
export async function fetchTransferQueue(): Promise<{
  rows: import("@care-by-ella/contracts").TransferQueueRow[];
}> {
  const res = await apiFetch(`${API_BASE}/employee-api/v1/transfer-reviews`, {
    headers: commonHeaders(),
    ...withCredentials,
  });
  return jsonOr401(res);
}

// TES-1032 — the nav badge's count: reviews waiting on the doctor.
export async function fetchTransferReviewCount(): Promise<{
  awaitingReview: number;
}> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/transfer-reviews/count`,
    { headers: commonHeaders(), ...withCredentials },
  );
  return jsonOr401(res);
}

// TES-457 — the doctor's decision on the transfer review. The body shape is
// the shared transferReviewDecisionSchema; the API validates it again.
// TES-1034 S7 — the transfer review's journal note: read (the server creates
// and prefills it on first read) and saved box by box. The decision stores
// its final version with the Webdoc receipt.
export async function fetchTransferReviewJournal(
  patientId: string,
): Promise<import("@care-by-ella/contracts").TransferReviewJournal> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/transfer-review/journal`,
    { headers: commonHeaders(), ...withCredentials },
  );
  return jsonOr401(res);
}

export async function saveTransferReviewJournal(
  patientId: string,
  input: import("@care-by-ella/contracts").UpdateJournalSections,
): Promise<import("@care-by-ella/contracts").TransferReviewJournal> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/transfer-review/journal`,
    {
      method: "PUT",
      headers: commonHeaders(),
      body: JSON.stringify(input),
      ...withCredentials,
    },
  );
  return jsonOr401(res);
}

// TES-1034 S8 (Emelie, point 6) — the doctor's exceptional correction of
// the patient's subscription choice, with its required reason.
export async function correctTransferPlan(
  patientId: string,
  body: import("@care-by-ella/contracts").TransferPlanCorrection,
): Promise<import("@care-by-ella/contracts").TransferReview> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/transfer-review/plan-correction`,
    {
      method: "POST",
      headers: commonHeaders(),
      body: JSON.stringify(body),
      ...withCredentials,
    },
  );
  return jsonOr401(res);
}

// TES-1057 / TES-1049 — the doctor's review of one prescription document.
export async function reviewPrescriptionEvidence(
  patientId: string,
  uploadId: string,
  body: import("@care-by-ella/contracts").PrescriptionEvidenceReviewUpdate,
): Promise<import("@care-by-ella/contracts").TransferReview> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/transfer-review/uploads/${uploadId}/evidence-review`,
    {
      method: "PUT",
      headers: commonHeaders(),
      body: JSON.stringify(body),
      ...withCredentials,
    },
  );
  return jsonOr401(res);
}

export async function decideTransferReview(
  patientId: string,
  decision: import("@care-by-ella/contracts").TransferReviewDecision,
): Promise<import("@care-by-ella/contracts").TransferReview> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/transfer-review/decide`,
    {
      method: "POST",
      headers: commonHeaders(),
      body: JSON.stringify(decision),
      ...withCredentials,
    },
  );
  return jsonOr401(res);
}

// TES-952 — "Lämna svar till patienten och fatta behandlingsbeslut": the
// lab answer, the decision and (on approval) the subscription link in ONE
// request, so the server can publish them in one transaction. It replaced
// the free-standing "Provsvar har kommit in" (TES-506), "Provsvar granskat"
// (TES-890), approve/reject and subscription-link calls on the patient page.
export async function publishTreatmentDecision(
  patientId: string,
  decision: PublishTreatmentDecision,
): Promise<PublishTreatmentDecisionResult> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/treatment-decision`,
    {
      method: "POST",
      headers: commonHeaders(),
      body: JSON.stringify(decision),
      ...withCredentials,
    },
  );
  return jsonOr401(res);
}

// TES-1291 — pause, resumption or end of a running treatment, from the same
// decision workspace.
export async function publishTreatmentStatusDecision(
  patientId: string,
  decision: PublishTreatmentStatusDecision,
): Promise<PublishTreatmentStatusDecisionResult> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/treatment-status-decision`,
    {
      method: "POST",
      headers: commonHeaders(),
      body: JSON.stringify(decision),
      ...withCredentials,
    },
  );
  return jsonOr401(res);
}

// TES-1061 — the note the decision is journaled in, and "Kopiera": that note
// with the decision on screen, stored as its newest final version. The
// publish names the returned version as `noteRevisionId`.
export async function fetchTreatmentDecisionNote(
  patientId: string,
): Promise<TreatmentDecisionNote> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/treatment-decision/note`,
    { headers: commonHeaders(), ...withCredentials },
  );
  return jsonOr401(res);
}

export async function finalizeTreatmentDecisionNote(
  patientId: string,
  draft: TreatmentDecisionNoteDraft,
): Promise<TreatmentDecisionNoteFinalized> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/treatment-decision/note/finalize`,
    {
      method: "POST",
      headers: commonHeaders(),
      body: JSON.stringify(draft),
      ...withCredentials,
    },
  );
  return jsonOr401(res);
}

// TES-862 / TES-869 (Ö-209, Ö-210) — the doctor corrects which care case the
// patient is in, in either direction.
//
// The direction is NOT chosen in the UI: it is derived from her current group,
// because there are only two groups and offering both would invite picking the
// one she is already in. The reason is structured (Ö-209) with free text
// required for `other`, exactly like the handover reasons next door.
//
// The response carries the ROUTING, and that is the part worth surfacing:
// Ö-210 forbids restarting onboarding or asking for a meeting that already
// happened, so the server names the next genuinely missing step and the client
// shows it back rather than guessing.
export async function transitionCareCase(
  patientId: string,
  body: TransitionCareCaseRequest,
): Promise<CareCaseTransitionResult> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/care-case/transition`,
    {
      method: "POST",
      headers: commonHeaders(),
      body: JSON.stringify(body),
      ...withCredentials,
    },
  );
  return jsonOr401(res);
}

// TES-890 (Ö-185) — "Provsvar granskat". The doctor records that he has been
// through a resulted order AND which of three conclusions he reached. Only
// `approved` opens the activation gate; the other two leave the order
// reviewed but hand the ball back to the patient.
//
// The route keeps its `/approve` path: renaming it would break nothing but
// buy nothing either, and approval is still what most calls mean.
// TES-863 (Ö-229) — the doctor corrects the patient's goal weight. `reason`
// is required by the server, so it is required here: a caller that could omit
// it would only discover the rule as a 400.
export async function adjustGoalWeight(
  patientId: string,
  kg: number | null,
  reason: string,
): Promise<{ previousKg: number | null; goalWeightKg: number | null }> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/goal-weight`,
    {
      method: "PUT",
      headers: commonHeaders(),
      body: JSON.stringify({ kg, reason }),
      ...withCredentials,
    },
  );
  return jsonOr401(res);
}

// TES-863 (Ö-211) — every measurement occasion, the superseded ones included,
// so the doctor sees the original beside any correction.
export async function fetchPatientMeasurements(
  patientId: string,
): Promise<{ measurements: Measurement[] }> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/measurements`,
    { headers: commonHeaders(), ...withCredentials },
  );
  return jsonOr401(res);
}

// TES-863 (Ö-211) — a correction lands BESIDE the occasion it replaces.
// `supersedesSessionId` and `reason` are both required by the server.
export async function correctMeasurementSession(
  patientId: string,
  body: CorrectMeasurementSession,
): Promise<unknown> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/measurements/corrections`,
    {
      method: "POST",
      headers: commonHeaders(),
      body: JSON.stringify(body),
      ...withCredentials,
    },
  );
  return jsonOr401(res);
}

// TES-1034 — a verified blood pressure / pulse as a NEW occasion of his own,
// beside hers. It never supersedes anything.
export async function recordVerifiedMeasurement(
  patientId: string,
  body: import("@care-by-ella/contracts").RecordVerifiedMeasurement,
): Promise<unknown> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/measurements/verified`,
    {
      method: "POST",
      headers: commonHeaders(),
      body: JSON.stringify(body),
      ...withCredentials,
    },
  );
  return jsonOr401(res);
}

// TES-1056 — the doctor's assessment of one of her blood pressure / pulse
// occasions. Only the status changes; the values never do.
export async function reviewMeasurementSession(
  patientId: string,
  sessionId: string,
  status: import("@care-by-ella/contracts").MeasurementReviewStatus,
): Promise<{
  sessionId: string;
  reviewStatus: import("@care-by-ella/contracts").MeasurementReviewStatus;
  reviewedAt: string | null;
}> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/measurement-sessions/${sessionId}/review`,
    {
      method: "PATCH",
      headers: commonHeaders(),
      body: JSON.stringify({ status }),
      ...withCredentials,
    },
  );
  return jsonOr401(res);
}

// TES-863 (Ö-211, groups 3/5/6) — medications, allergies and reproductive
// status: what she declared, and every correction beside it.
export async function fetchMedicalProfile(
  patientId: string,
): Promise<MedicalProfileResponse> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/medical-profile`,
    { headers: commonHeaders(), ...withCredentials },
  );
  return jsonOr401(res);
}

export async function correctMedicalProfile(
  patientId: string,
  body: CorrectMedicalProfile,
): Promise<MedicalProfileEntryDto> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/medical-profile/corrections`,
    {
      method: "POST",
      headers: commonHeaders(),
      body: JSON.stringify(body),
      ...withCredentials,
    },
  );
  return jsonOr401(res);
}

// TES-201 — patient-facing lab feedback. The doctor opens a resulted order's
// feedback panel (GET: the AI-generated draft merged with any saved edits),
// tweaks the per-analyte comments + overall summary, then either saves a draft
// (PUT, no gates touched) or "Godkänn och skicka till patient" (POST/send,
// which makes the feedback visible to the patient and approves the order if it
// wasn't already — a superset of the CAR-117 approve gate).
// TES-950 — what the completion picker shows beside each option.
export async function fetchCompletionHints(
  patientId: string,
): Promise<CompletionHints> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/completion-hints`,
    { headers: commonHeaders(), ...withCredentials },
  );
  return jsonOr401(res);
}

// TES-590 — CP-06's checklist + the evidence files in the same case.
export async function fetchMedicalEvidence(
  patientId: string,
): Promise<ClinicianMedicalEvidence> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/medical-evidence`,
    { headers: commonHeaders(), ...withCredentials },
  );
  return jsonOr401(res);
}

// TES-949 — "Inskickat medicinskt underlag" across the whole journey.
export async function fetchEvidenceSummary(
  patientId: string,
): Promise<EvidenceSummary> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/evidence-summary`,
    { headers: commonHeaders(), ...withCredentials },
  );
  return jsonOr401(res);
}

// TES-1125 — "Tidigare provsvar är bedömt av läkare": mark one earlier lab
// file assessed. Returns the card as it now reads.
export async function assessEarlierLabUpload(
  patientId: string,
  uploadId: string,
): Promise<EvidenceSummary> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/uploads/${uploadId}/assessment`,
    { method: "PUT", headers: commonHeaders(), ...withCredentials },
  );
  return jsonOr401(res);
}

export async function fetchLabFeedback(
  patientId: string,
  orderId: string,
): Promise<ClinicianLabFeedback> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/lab-orders/${orderId}/feedback`,
    { headers: commonHeaders(), ...withCredentials },
  );
  return jsonOr401(res);
}

// TES-442 — "Remissnummer i Webdoc (valfritt)" on a lab order. null clears.
export async function setLabOrderRequisitionRef(
  patientId: string,
  orderId: string,
  requisitionRef: string | null,
): Promise<{ id: string; requisitionRef: string | null }> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/lab-orders/${orderId}/requisition-ref`,
    {
      method: "PUT",
      headers: commonHeaders(),
      body: JSON.stringify({ requisitionRef }),
      ...withCredentials,
    },
  );
  return jsonOr401(res);
}

export async function saveLabFeedback(
  patientId: string,
  orderId: string,
  input: ClinicianLabFeedbackInput,
): Promise<ClinicianLabFeedback> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/lab-orders/${orderId}/feedback`,
    {
      method: "PUT",
      headers: commonHeaders(),
      body: JSON.stringify(input),
      ...withCredentials,
    },
  );
  return jsonOr401(res);
}

// TES-1370 (Ö-582) — approve one patient text exactly as it stands (the
// summary, or one value's explanation), or withdraw the approval.
export async function approveLabFeedbackItem(
  patientId: string,
  orderId: string,
  input: ClinicianLabFeedbackApprovalInput,
): Promise<ClinicianLabFeedback> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/lab-orders/${orderId}/feedback/approval`,
    {
      method: "POST",
      headers: commonHeaders(),
      body: JSON.stringify(input),
      ...withCredentials,
    },
  );
  return jsonOr401(res);
}

export async function sendLabFeedback(
  patientId: string,
  orderId: string,
  input: ClinicianLabFeedbackInput,
): Promise<ClinicianLabFeedback> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/lab-orders/${orderId}/feedback/send`,
    {
      method: "POST",
      headers: commonHeaders(),
      body: JSON.stringify(input),
      ...withCredentials,
    },
  );
  return jsonOr401(res);
}

// CAR-115 — generate + send a web subscription link to the patient. Returns
// the URL so the doctor can copy/share it during the call.
// TES-907 (Ö-221) — the preview, from tenant config.
//
// Fetched rather than hardcoded in the component: the price the doctor is
// shown and the price Stripe charges have to come from one place, or the
// preview eventually lies about what she will pay.
export async function fetchSubscriptionPlans(): Promise<{
  plans: SubscriptionLinkPreview[];
}> {
  const res = await apiFetch(`${API_BASE}/employee-api/v1/subscription-plans`, {
    headers: commonHeaders(),
    ...withCredentials,
  });
  return jsonOr401(res);
}

// TES-907 (Ö-221) — `choice` is REQUIRED because the server requires it.
// "Inget förvalt" is enforced there, not here; an optional argument would only
// turn the rule into a 400 the caller discovers at runtime.
export async function sendSubscriptionLink(
  patientId: string,
  choice: { plan: SubscriptionPlan; maintenanceBasis?: string },
): Promise<{
  url: string;
  expiresAt: string;
  emailed: boolean;
  plan: SubscriptionPlan;
}> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/subscription-link`,
    {
      method: "POST",
      headers: commonHeaders(),
      body: JSON.stringify(choice),
      ...withCredentials,
    },
  );
  return jsonOr401(res);
}

export async function fetchClinicianMeetings(): Promise<{
  meetings: ClinicianMeeting[];
}> {
  const res = await apiFetch(`${API_BASE}/employee-api/v1/meetings`, {
    headers: commonHeaders(),
    ...withCredentials,
  });
  return jsonOr401(res);
}

// TES-1233 — the "Möten" calendar: meetings, schedule windows and free slots
// in [from, to). The server refuses ranges over six weeks.
export async function fetchMeetingCalendar(
  from: Date,
  to: Date,
): Promise<ClinicianMeetingCalendar> {
  const q = new URLSearchParams({
    from: from.toISOString(),
    to: to.toISOString(),
  });
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/meetings/calendar?${q.toString()}`,
    { headers: commonHeaders(), ...withCredentials },
  );
  return jsonOr401(res);
}

// TES-434 B — the clinic's aggregate patient rating of care meetings.
export async function fetchMeetingRatingStats(): Promise<MeetingRatingStats> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/meetings/rating-stats`,
    {
      headers: commonHeaders(),
      ...withCredentials,
    },
  );
  return jsonOr401(res);
}

export async function mintClinicianMeetingToken(meetingId: string): Promise<{
  meetingId: string;
  roomName: string;
  roomUrl: string;
  token: string;
  expiresAt: string;
}> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/meetings/${meetingId}/token`,
    { method: "POST", headers: commonHeaders(), ...withCredentials },
  );
  return jsonOr401(res);
}

// TES-1383 — the open meeting view's poll: still on, cancelled, or moved (and
// where to). Status and time only; not an audited patient read.
export async function fetchClinicianMeetingStatus(
  meetingId: string,
): Promise<MeetingLiveStatus> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/meetings/${meetingId}/status`,
    { headers: commonHeaders(), ...withCredentials },
  );
  return jsonOr401(res);
}

// TES-978 — the doctor hung up and chose "Avsluta mötet": the session ends
// for both (completed, join links gone, CP-05 started). Idempotent.
export async function endClinicianMeeting(meetingId: string): Promise<{
  id: string;
  status: string;
  sessionEndedAt: string | null;
}> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/meetings/${meetingId}/end`,
    { method: "POST", headers: commonHeaders(), ...withCredentials },
  );
  return jsonOr401(res);
}

// TES-1671 — "Patienten kom inte": the doctor closes his meeting as a
// no-show. Never advances her journey. Answers with the meeting's state,
// which is where it went if something else closed it first.
export async function markClinicianMeetingNoShow(meetingId: string): Promise<{
  id: string;
  status: string;
  sessionEndedAt: string | null;
}> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/meetings/${meetingId}/no-show`,
    { method: "POST", headers: commonHeaders(), ...withCredentials },
  );
  return jsonOr401(res);
}

// TES-291 [J10] — the managed care-meeting prep view (read-only aggregation:
// booking + payment + pre-consult summary + risk flags + journey + goals).
export async function fetchMeetingPrep(
  patientId: string,
  meetingId: string,
): Promise<MeetingPrep> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/meetings/${meetingId}/prep`,
    { headers: commonHeaders(), ...withCredentials },
  );
  return jsonOr401(res);
}

// TES-291 [J10] — persist the post-meeting documentation + decision artifact.
// The API actuates the decisions (journey advance, coach handoff, patient
// feedback, next-flow trigger) server-side by reusing the platform primitives.
export async function documentMeeting(
  patientId: string,
  meetingId: string,
  input: DocumentMeetingRequest,
): Promise<MeetingDocumentation> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/meetings/${meetingId}/document`,
    {
      method: "POST",
      headers: commonHeaders(),
      body: JSON.stringify(input),
      ...withCredentials,
    },
  );
  return jsonOr401(res);
}

// TES-1185 — the meeting's final step with the positive decision in it: one
// request, one transaction (MeetingDecisionService).
export async function documentMeetingAndDecide(
  patientId: string,
  meetingId: string,
  input: DocumentMeetingAndDecideRequest,
): Promise<{
  meeting: MeetingDocumentation;
  decision: PublishTreatmentDecisionResult;
}> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/meetings/${meetingId}/document-and-decide`,
    {
      method: "POST",
      headers: commonHeaders(),
      body: JSON.stringify(input),
      ...withCredentials,
    },
  );
  return jsonOr401(res);
}

// TES-904 — the meeting's journal note, seven headed sections. The first read
// creates them (carrying the old assessment and plan note over) and prefills
// the empty ones from her forms.
// TES-1289 — "Sammanfattning efter vårdmötet": the server's draft, the
// doctor's save ("Uppdatera utkastet" / "Behåll min text" too), and the
// publish of the text she previewed.
export async function fetchMeetingSummary(
  patientId: string,
  meetingId: string,
): Promise<MeetingSummaryView> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/meetings/${meetingId}/summary`,
    { headers: commonHeaders(), ...withCredentials },
  );
  return jsonOr401(res);
}

export async function saveMeetingSummary(
  patientId: string,
  meetingId: string,
  input: SaveMeetingSummaryRequest,
): Promise<MeetingSummaryView> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/meetings/${meetingId}/summary`,
    {
      method: "PUT",
      headers: commonHeaders(),
      body: JSON.stringify(input),
      ...withCredentials,
    },
  );
  return jsonOr401(res);
}

export async function publishMeetingSummary(
  patientId: string,
  meetingId: string,
  input: PublishMeetingSummaryRequest,
): Promise<MeetingSummaryView> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/meetings/${meetingId}/summary/publish`,
    {
      method: "POST",
      headers: commonHeaders(),
      body: JSON.stringify(input),
      ...withCredentials,
    },
  );
  return jsonOr401(res);
}

export async function fetchMeetingJournal(
  patientId: string,
  meetingId: string,
): Promise<MeetingJournal> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/meetings/${meetingId}/journal`,
    { headers: commonHeaders(), ...withCredentials },
  );
  return jsonOr401(res);
}

// TES-1019 — "Sammanfatta" / "Sammanställ" for one heading of the meeting's
// note. Always a draft for that box: the model's text, or the deterministic
// text with the reason when the model's cannot be offered.
export async function generateJournalSectionAi(
  patientId: string,
  meetingId: string,
  input: JournalSectionAiRequest,
): Promise<JournalSectionAiResult> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/meetings/${meetingId}/journal/ai`,
    {
      method: "POST",
      headers: commonHeaders(),
      body: JSON.stringify(input),
      ...withCredentials,
    },
  );
  return jsonOr401(res);
}

// TES-1034 — the same two actions for one heading of the prescription
// transfer review's note. The review is the patient's open one (or her latest).
export async function generateTransferJournalSectionAi(
  patientId: string,
  input: JournalSectionAiRequest,
): Promise<JournalSectionAiResult> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/transfer-review/journal/ai`,
    {
      method: "POST",
      headers: commonHeaders(),
      body: JSON.stringify(input),
      ...withCredentials,
    },
  );
  return jsonOr401(res);
}

export async function saveMeetingJournal(
  patientId: string,
  meetingId: string,
  input: UpdateMeetingJournal,
): Promise<MeetingJournal> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/meetings/${meetingId}/journal`,
    {
      method: "PUT",
      headers: commonHeaders(),
      body: JSON.stringify(input),
      ...withCredentials,
    },
  );
  return jsonOr401(res);
}

// TES-1060 (TES-1054 point C) — the Kompletteringsanteckning of one
// completion round: read, save boxes, compile for copying (stored as the
// final version), mark "Införd i Webdoc".
export async function fetchCompletionNote(
  patientId: string,
  roundId: string,
): Promise<CompletionNote> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/completion-rounds/${roundId}/note`,
    { headers: commonHeaders(), ...withCredentials },
  );
  return jsonOr401(res);
}

export async function saveCompletionNote(
  patientId: string,
  roundId: string,
  input: UpdateCompletionNote,
): Promise<CompletionNote> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/completion-rounds/${roundId}/note`,
    {
      method: "PUT",
      headers: commonHeaders(),
      body: JSON.stringify(input),
      ...withCredentials,
    },
  );
  return jsonOr401(res);
}

export async function finalizeCompletionNote(
  patientId: string,
  roundId: string,
): Promise<CompletionNoteFinalized> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/completion-rounds/${roundId}/note/finalize`,
    { method: "POST", headers: commonHeaders(), ...withCredentials },
  );
  return jsonOr401(res);
}

export async function markCompletionNoteWebdoc(
  patientId: string,
  roundId: string,
  revisionId: string,
): Promise<JournalWebdocStatus> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/completion-rounds/${roundId}/note/webdoc`,
    {
      method: "POST",
      headers: commonHeaders(),
      body: JSON.stringify({ revisionId }),
      ...withCredentials,
    },
  );
  return jsonOr401(res);
}

// TES-1060 (TES-1054 point E) — "Hantera provsvar från Webdoc" on the
// current case's order. `held`: other items are still open, so the review
// waits in the note's draft and nothing is stamped yet.
export async function recordLabWebdocAssessment(
  patientId: string,
  orderId: string,
  input: LabWebdocAssessment,
): Promise<LabWebdocAssessmentResult> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/lab-orders/${orderId}/webdoc-assessment`,
    {
      method: "POST",
      headers: commonHeaders(),
      body: JSON.stringify(input),
      ...withCredentials,
    },
  );
  return jsonOr401(res);
}

// TES-1019 — the meeting's shorthand ("Stödord"), "Sammanställ journal" and
// "Markera som införd i Webdoc" are gone from the admin: the note is
// assembled in the Webdoc step, and the publish (documentMeeting) stores its
// final version and receipt.

export async function cancelClinicianMeeting(
  meetingId: string,
  reason?: string,
): Promise<{ id: string; status: string }> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/meetings/${meetingId}/cancel`,
    {
      method: "POST",
      headers: commonHeaders(),
      body: JSON.stringify(reason ? { reason } : {}),
      ...withCredentials,
    },
  );
  return jsonOr401(res);
}

// CAR-17 — patient ↔ doctor chat. ChatMessage + its sender/kind enums
// live in `@care-by-ella/contracts` (CAR-67) and are re-exported at the
// top of this module for existing call sites.

export interface PatientChat {
  conversation: {
    id: string;
    patientId: string;
    lastMessageAt: string | null;
  };
  messages: ChatMessage[];
  // TES-1145 — the doctor chat is closed for her (her app's last reported
  // contract, or the API's DOCTOR_CHAT_CLOSED_FOR_ALL). Absent from an API
  // before TES-1145, which reads as open.
  doctorChatClosed?: boolean;
}

// Chat split — every call is channel-scoped ('doctor' | 'coach'). Reads are
// open to both staff roles; writes are channel-locked by role server-side.
export type ChatChannel = "doctor" | "coach";

export interface ChatInboxRow {
  id: string;
  patientId: string;
  channel: ChatChannel;
  unreadCount: number;
  // Optional, like the other queues' labels — an older API omits it.
  patientName?: string | null;
  personalNumberLast6: string;
  lastMessageAt: string | null;
  // TES-889 (Ö-216) — the oldest UNANSWERED patient message in the thread,
  // and whether it has been sitting there for 24 staffed weekday hours.
  // Null when nothing is waiting on us. Optional: an older API omits both.
  waitingSince?: string | null;
  delayed?: boolean;
  latestMessage: {
    id: string;
    kind: string;
    senderType: string;
    body: string;
    createdAt: string;
  } | null;
}

export async function fetchChatInbox(): Promise<{
  conversations: ChatInboxRow[];
}> {
  const res = await apiFetch(`${API_BASE}/employee-api/v1/chat/conversations`, {
    headers: commonHeaders(),
    ...withCredentials,
  });
  return jsonOr401(res);
}

export async function fetchPatientChat(
  patientId: string,
  channel: ChatChannel,
  since?: string,
  before?: string,
): Promise<PatientChat> {
  const qs = since
    ? `?since=${encodeURIComponent(since)}`
    : before
      ? `?before=${encodeURIComponent(before)}`
      : "";
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/chat/patients/${patientId}/${channel}/messages${qs}`,
    { headers: commonHeaders(), ...withCredentials },
  );
  return jsonOr401(res);
}

export async function sendStaffChatMessage(
  patientId: string,
  channel: ChatChannel,
  body: string,
): Promise<{ id: string; createdAt: string }> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/chat/patients/${patientId}/${channel}/messages`,
    {
      method: "POST",
      headers: commonHeaders(),
      body: JSON.stringify({ body }),
      ...withCredentials,
    },
  );
  return jsonOr401(res);
}

// TES-784 — the clinician edits her OWN message. The ownership check lives in
// the service (senderType + senderId), so a coach never reaches the doctor's
// text and neither reaches the patient's.
export async function editStaffChatMessage(
  patientId: string,
  messageId: string,
  body: string,
): Promise<{ id: string; editedAt: string | null }> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/chat/patients/${patientId}/messages/${messageId}`,
    {
      method: "PATCH",
      headers: commonHeaders(),
      body: JSON.stringify({ body }),
      ...withCredentials,
    },
  );
  return jsonOr401(res);
}

// TES-784 — and deletes it. Soft delete: the text is replaced server-side and
// the patient sees that something was removed.
export async function deleteStaffChatMessage(
  patientId: string,
  messageId: string,
): Promise<{ id: string }> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/chat/patients/${patientId}/messages/${messageId}`,
    { method: "DELETE", headers: commonHeaders(), ...withCredentials },
  );
  return jsonOr401(res);
}

export async function markStaffChatRead(
  patientId: string,
  channel: ChatChannel,
): Promise<{ ok: true }> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/chat/patients/${patientId}/${channel}/read`,
    {
      method: "POST",
      headers: commonHeaders(),
      body: JSON.stringify({}),
      ...withCredentials,
    },
  );
  return jsonOr401(res);
}

// CAR-55 — admin onboarding for a new clinician. Caller's tenant is
// resolved server-side from the JWT, so this body carries only the new
// doctor's identity. PN format is enforced server-side (12 digits,
// plausible birth year); client-side validation runs in the form too
// for fast feedback.
export interface CreateClinicianInput {
  displayName: string;
  email: string;
  personalNumber: string;
}

export async function createClinician(
  input: CreateClinicianInput,
): Promise<{ id: string; displayName: string; email: string }> {
  const res = await apiFetch(`${API_BASE}/employee-api/v1/clinicians`, {
    method: "POST",
    headers: commonHeaders(),
    body: JSON.stringify(input),
    ...withCredentials,
  });
  return jsonOr401(res);
}

// Media support — the content-image library (list + upload).
export async function fetchMediaAssets(): Promise<{
  assets: import("@care-by-ella/contracts").MediaAssetMeta[];
}> {
  const res = await apiFetch(`${API_BASE}/employee-api/v1/media`, {
    headers: commonHeaders(),
    ...withCredentials,
  });
  return jsonOr401(res);
}

export async function uploadMediaAsset(
  input: import("@care-by-ella/contracts").MediaUploadInput,
): Promise<import("@care-by-ella/contracts").MediaAssetMeta> {
  const res = await apiFetch(`${API_BASE}/employee-api/v1/media`, {
    method: "POST",
    headers: { ...commonHeaders(), "Content-Type": "application/json" },
    ...withCredentials,
    body: JSON.stringify(input),
  });
  return jsonOr401(res);
}

// TES-497 — the care team's own portrait. The API binds the slot to the
// caller (TES-719: careTeamPortraitKey of the employee id); the client sends
// bytes only. Replace = version++ on the same key, so the returned meta carries the
// cache-busting version for the preview.
export async function fetchMyPortrait(): Promise<
  import("@care-by-ella/contracts").CareTeamPortraitState
> {
  const res = await apiFetch(`${API_BASE}/employee-api/v1/media/portrait`, {
    headers: commonHeaders(),
    ...withCredentials,
  });
  return jsonOr401(res);
}

export async function uploadMyPortrait(
  input: import("@care-by-ella/contracts").CareTeamPortraitUploadInput,
): Promise<import("@care-by-ella/contracts").MediaAssetMeta> {
  const res = await apiFetch(`${API_BASE}/employee-api/v1/media/portrait`, {
    method: "PUT",
    headers: { ...commonHeaders(), "Content-Type": "application/json" },
    ...withCredentials,
    body: JSON.stringify(input),
  });
  return jsonOr401(res);
}

// TES-519 — the two CTAs behind the Incheckningar and Medicinska larm lanes.
// Both endpoints existed (doctor-review.controller); neither had a client.

/** "Markera som granskad" on a weekly check-in. Doctor or coach. */
export async function markSubmissionReviewed(
  patientId: string,
  submissionId: string,
): Promise<{ id: string; reviewedAt: string | null }> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/form-submissions/${submissionId}/reviewed`,
    { method: "POST", headers: commonHeaders(), ...withCredentials },
  );
  return jsonOr401(res);
}

// TES-899 — the coach's only two ways to finish a weekly check-in. Every
// surface that answers a check-in calls these, never the generic chat send:
// the reply is linked to the check-in, and a repeat (double click, reload, a
// second view) returns the first result instead of sending again.

/** "Skicka svar och slutför". Coach only. */
export async function sendCheckinCoachReply(
  patientId: string,
  submissionId: string,
  input: SendCheckinCoachReplyRequest,
): Promise<CheckinCoachActionResult> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/checkins/${submissionId}/reply`,
    {
      method: "POST",
      headers: commonHeaders(),
      body: JSON.stringify(input),
      ...withCredentials,
    },
  );
  return jsonOr401(res);
}

/** "Avsluta utan svar" — `reason` is mandatory. Coach only. */
export async function closeCheckinWithoutReply(
  patientId: string,
  submissionId: string,
  body: CloseCheckinWithoutReplyRequest,
): Promise<CheckinCoachActionResult> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/checkins/${submissionId}/close-without-reply`,
    {
      method: "POST",
      headers: commonHeaders(),
      body: JSON.stringify(body),
      ...withCredentials,
    },
  );
  return jsonOr401(res);
}

// TES-899 part 4 — how a check-in was handled: the reply it was answered
// with (read from the coach thread) or why it was closed. Coach only.
export async function fetchCheckinHandling(
  patientId: string,
  submissionId: string,
): Promise<CheckinCoachHandlingDetail> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/checkins/${submissionId}`,
    { headers: commonHeaders(), ...withCredentials },
  );
  return jsonOr401(res);
}

/** Admin copy for the two actions' refusals, by error code. */
export function checkinActionErrorText(e: unknown): string {
  switch (e instanceof Error ? e.message : "") {
    case "checkin_already_answered":
      return "Incheckningen är redan besvarad.";
    case "checkin_already_closed":
      return "Incheckningen är redan avslutad utan svar.";
    case "checkin_close_reason_required":
      return "Välj varför incheckningen avslutas utan svar. Vid ”Annat” behövs en kort beskrivning.";
    case "coach_access_inactive":
      return "Patienten har Ella Underhåll, som inte innehåller coachkontakt.";
    default:
      return "Det gick inte att spara. Försök igen.";
  }
}

/** TES-676 — confirm receipt of an open medical risk flag. Doctor only. */
export async function acknowledgeRiskFlag(
  patientId: string,
  flagId: string,
): Promise<{ id: string; acknowledgedAt: string }> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/medical-risk-flags/${flagId}/acknowledge`,
    { method: "POST", headers: commonHeaders(), ...withCredentials },
  );
  return jsonOr401(res);
}

/** Close ("avsluta") an open medical risk flag. Doctor only. */
export async function clearRiskFlag(
  patientId: string,
  flagId: string,
): Promise<{ id: string; clearedAt: string }> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/medical-risk-flags/${flagId}/clear`,
    { method: "POST", headers: commonHeaders(), ...withCredentials },
  );
  return jsonOr401(res);
}

// TES-943 — the code-computed form facts and flags. Not behind the AI switch:
// the overview must work on a box where AI is off (TES-941).
export async function fetchPatientFormFacts(
  patientId: string,
): Promise<PatientFormFacts> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/form-facts`,
    { headers: commonHeaders(), ...withCredentials },
  );
  return jsonOr401(res);
}

// TES-708 — AI-assist form overview (TES-706). The switch lives on the
// server (`AI_ASSIST_ENABLED`); with it off every ai-assist route 404s with
// `ai_assist_disabled`, which the panel turns into "render nothing". That is
// the only feature-flag channel the admin has — there is no config endpoint.
export class AiAssistDisabledError extends Error {}

async function aiAssistJson<T>(res: Response): Promise<T> {
  if (res.status === 404) {
    let code: string | null = null;
    try {
      code = ((await res.clone().json()) as { error?: string })?.error ?? null;
    } catch {
      // not JSON
    }
    if (code === "ai_assist_disabled") throw new AiAssistDisabledError();
  }
  return jsonOr401(res);
}

export async function fetchAiFormOverview(
  patientId: string,
): Promise<AiAssistFormOverview> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/ai-assist/form-overview`,
    { headers: commonHeaders(), ...withCredentials },
  );
  return aiAssistJson(res);
}

export async function generateAiFormOverview(
  patientId: string,
): Promise<AiAssistFormOverview> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/ai-assist/form-overview`,
    { method: "POST", headers: commonHeaders(), ...withCredentials },
  );
  return aiAssistJson(res);
}

// TES-709 — "Använd": records which field a draft text went into. The text
// itself stays in the browser; the server only learns the field.
export async function markAiDraftUsed(
  patientId: string,
  draftId: string,
  field: AiAssistUsedField,
): Promise<void> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/ai-assist/drafts/${draftId}/used`,
    {
      method: "POST",
      headers: commonHeaders(),
      body: JSON.stringify({ field }),
      ...withCredentials,
    },
  );
  if (res.status === 204) return;
  await aiAssistJson(res);
}

// TES-710 — the coach's reply draft for one weekly check-in.
export async function fetchAiCoachReply(
  patientId: string,
  submissionId: string,
): Promise<AiAssistCoachReply> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/ai-assist/checkins/${submissionId}/reply`,
    { headers: commonHeaders(), ...withCredentials },
  );
  return aiAssistJson(res);
}

export async function generateAiCoachReply(
  patientId: string,
  submissionId: string,
): Promise<AiAssistCoachReply> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/ai-assist/checkins/${submissionId}/reply`,
    { method: "POST", headers: commonHeaders(), ...withCredentials },
  );
  return aiAssistJson(res);
}

// TES-818 punkt 1 — F-01 as the coach's persisting start context.
//
// `@Roles("coach", "doctor")`, and the server decides from the JWT which of
// the two shapes comes back: a coach gets her summary plus the answers she
// may read, with the doctor-only rows present but empty. There is no
// parameter here to ask for the other shape, which is the point.
export async function fetchF01StartContext(
  patientId: string,
): Promise<F01StartContext> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/f01-start-context`,
    { headers: commonHeaders(), ...withCredentials },
  );
  return jsonOr401(res);
}

// ---------------------------------------------------------------------------
// TES-745/746/747 — the coach's patient memory ("Patientbilden")
// ---------------------------------------------------------------------------
//
// The package has been in main since 7d660461 with no admin surface reading
// it: memory was written and stored, and no coach could see a word of it.
// These are the reads and writes that panel needs.
//
// Both routes below are @Roles("coach", "doctor"), so a 403 here is not an
// ordinary error — it is an employee the panel was never meant for, and the
// caller hides itself rather than showing a failure.

export async function fetchCoachMemory(
  patientId: string,
): Promise<CoachMemory> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/memory`,
    { headers: commonHeaders(), ...withCredentials },
  );
  return jsonOr401(res);
}

export async function createCoachMemoryItem(
  patientId: string,
  body: CreateCoachMemoryItemRequest,
): Promise<CoachMemoryItem> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/memory`,
    {
      method: "POST",
      headers: commonHeaders(),
      body: JSON.stringify(body),
      ...withCredentials,
    },
  );
  return jsonOr401(res);
}

// A correction, not an edit: the API supersedes the old row and returns the
// NEW one, so the previous wording survives. The item that comes back
// therefore has a different `id` than the one passed in — callers refetch
// instead of patching the old row in place.
export async function reviseCoachMemoryItem(
  patientId: string,
  itemId: string,
  body: ReviseCoachMemoryItemRequest,
): Promise<CoachMemoryItem> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/memory/${itemId}`,
    {
      method: "PATCH",
      headers: commonHeaders(),
      body: JSON.stringify(body),
      ...withCredentials,
    },
  );
  return jsonOr401(res);
}

// Only an `ai_observation` can be confirmed — the API answers 400
// `coach_memory_not_a_hypothesis` for anything else. Confirming is what turns
// a machine's guess into something a named human stands behind.
export async function confirmCoachMemoryItem(
  patientId: string,
  itemId: string,
): Promise<CoachMemoryItem> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/memory/${itemId}/confirm`,
    { method: "POST", headers: commonHeaders(), ...withCredentials },
  );
  return jsonOr401(res);
}

// DELETE by verb only. The row is retired with a lifecycle and stays in the
// table, which is what keeps "who said what, when" answerable — so the body is
// required and says WHICH kind of removal this is.
export async function retireCoachMemoryItem(
  patientId: string,
  itemId: string,
  body: RetireCoachMemoryItemRequest,
): Promise<CoachMemoryItem> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/memory/${itemId}`,
    {
      method: "DELETE",
      headers: commonHeaders(),
      body: JSON.stringify(body),
      ...withCredentials,
    },
  );
  return jsonOr401(res);
}

// TES-749 — one route, two shapes, and the SERVER picks which by the caller's
// role (never a query parameter — a client-supplied role is how a coach ended
// up able to read clinical free text in TES-648/651). A coach gets the neutral
// shape; only the doctor's carries `state`, which is what callers discriminate
// on.
//
// 404 `cycle_not_found` is an ordinary answer, not a failure: a patient with
// no prescription cycle yet has no loop to show.
export async function fetchPatientCycleView(
  patientId: string,
): Promise<CoachCycleView | DoctorCycleView> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/cycle-view`,
    { headers: commonHeaders(), ...withCredentials },
  );
  return jsonOr401(res);
}

// ---------------------------------------------------------------------------
// TES-986 (Ö-266) — medical review cases. Doctor only; a coach gets 403.
// ---------------------------------------------------------------------------

/** The patient's medical review cases, open and RED first. No originals. */
export async function listMedicalReviewCases(
  patientId: string,
): Promise<MedicalReviewCaseSummary[]> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/medical-review-cases`,
    { headers: commonHeaders(), ...withCredentials },
  );
  return jsonOr401(res);
}

/** Open a case: returns the originals and records that you have seen them. */
export async function openMedicalReviewCase(
  patientId: string,
  caseId: string,
): Promise<MedicalReviewCaseDetail> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/medical-review-cases/${caseId}/open`,
    { method: "POST", headers: commonHeaders(), ...withCredentials },
  );
  return jsonOr401(res);
}

/**
 * TES-1040 — the admin's read of a case's originals, in her read-only view.
 * Same shape as the doctor's opening, but a GET: it records no opening (so
 * it cannot stand in for the doctor's), and quoted chat or reflection text
 * comes back masked (MEDICAL_REVIEW_CASE_ADMIN_MASK). Admin-only.
 */
export async function fetchMedicalReviewCaseForSupport(
  patientId: string,
  caseId: string,
): Promise<MedicalReviewCaseDetail> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/medical-review-cases/${caseId}`,
    { headers: commonHeaders(), ...withCredentials },
  );
  return jsonOr401(res);
}

/**
 * TES-1120 — "Svara som medicinskt besked": publishes the answer as a signed
 * medical notice and handles the case. Doctor only; 409 until this doctor
 * has opened the latest originals.
 */
export async function answerMedicalReviewCase(
  patientId: string,
  caseId: string,
  body: MedicalQuestionAnswerRequest,
): Promise<MedicalQuestionAnswerResponse> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/medical-review-cases/${caseId}/answer`,
    {
      method: "POST",
      headers: commonHeaders(),
      body: JSON.stringify(body),
      ...withCredentials,
    },
  );
  return jsonOr401(res);
}

/**
 * TES-1147 — "Skicka besked": a medical notice the doctor sends with no
 * medical question behind it, in either TES-1332 mode, or (with
 * `followUpOf`) the queue's "Skicka nytt besked". Doctor only.
 */
export async function sendDoctorNotice(
  patientId: string,
  body: DoctorNoticeRequest,
): Promise<DoctorNoticeResponse> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/doctor-notices`,
    {
      method: "POST",
      headers: commonHeaders(),
      body: JSON.stringify(body),
      ...withCredentials,
    },
  );
  return jsonOr401(res);
}

/**
 * TES-1149 — the questions asked of her with "Besked med fråga", each with
 * its notice, status and her answer. Doctor and admin roles.
 */
export async function fetchDoctorQuestions(
  patientId: string,
): Promise<DoctorQuestionList> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/doctor-questions`,
    { headers: commonHeaders(), ...withCredentials },
  );
  return jsonOr401(res);
}

/**
 * TES-1332 — "Hanterat – inget nytt besked behövs" on the queue's "Svar på
 * läkarens fråga". Doctor only; idempotent.
 */
export async function markDoctorQuestionHandled(
  patientId: string,
  taskId: string,
): Promise<DoctorQuestionHandledResponse> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/doctor-questions/${taskId}/handled`,
    { method: "POST", headers: commonHeaders(), ...withCredentials },
  );
  return jsonOr401(res);
}

/**
 * TES-1335 — "Starta medicinsk åtgärd": the chosen action takes her answer
 * over (Hanterad, with the action recorded). Doctor only; idempotent.
 */
export async function startDoctorQuestionMedicalAction(
  patientId: string,
  taskId: string,
  action: DoctorQuestionMedicalAction,
): Promise<DoctorQuestionHandledResponse> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/doctor-questions/${taskId}/medical-action`,
    {
      method: "POST",
      headers: commonHeaders(),
      body: JSON.stringify({ action }),
      ...withCredentials,
    },
  );
  return jsonOr401(res);
}

/**
 * TES-1335 — "Återkalla frågan" on a question she has not answered. Doctor
 * only; idempotent. Refused (409 doctor_question_answered_not_withdrawable)
 * once she has answered.
 */
export async function withdrawDoctorQuestion(
  patientId: string,
  taskId: string,
): Promise<DoctorQuestionWithdrawnResponse> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/doctor-questions/${taskId}/withdraw`,
    { method: "POST", headers: commonHeaders(), ...withCredentials },
  );
  return jsonOr401(res);
}

/** "Granskad" — refused (409) until the latest originals were opened. */
export async function reviewMedicalReviewCase(
  patientId: string,
  caseId: string,
): Promise<MedicalReviewCaseSummary> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/medical-review-cases/${caseId}/review`,
    { method: "POST", headers: commonHeaders(), ...withCredentials },
  );
  return jsonOr401(res);
}

/**
 * TES-1169 — "Begär komplettering" on the patient overview: whether there is
 * an active medical case (and if not, why), and the request itself. Doctor
 * only, like the meeting's komplettering.
 */
export async function fetchOverviewCompletionStatus(
  patientId: string,
): Promise<ActiveMedicalCaseResponse> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/completion-request`,
    { headers: commonHeaders(), ...withCredentials },
  );
  return jsonOr401(res);
}

export async function requestOverviewCompletion(
  patientId: string,
  body: OverviewCompletionRequest,
): Promise<OverviewCompletionRequestResult> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/completion-request`,
    {
      method: "POST",
      headers: commonHeaders(),
      body: JSON.stringify(body),
      ...withCredentials,
    },
  );
  return jsonOr401(res);
}

/**
 * TES-1602 (TES-1600 S2) — "Kräv vårdmöte" for a patient on the optional
 * care meeting track. Doctor only; everyone else gets 403.
 */
export async function fetchCareMeetingRequirement(
  patientId: string,
): Promise<CareMeetingRequirementView> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/care-meeting-requirement`,
    { headers: commonHeaders(), ...withCredentials },
  );
  return jsonOr401(res);
}

export async function requireCareMeeting(
  patientId: string,
): Promise<CareMeetingRequirementView> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/care-meeting-requirement`,
    { method: "POST", headers: commonHeaders(), ...withCredentials },
  );
  return jsonOr401(res);
}

/**
 * TES-1648 (Ö-1016) — the written track's three patient photos and the
 * doctor's review ("Godkänn bilder" / "Begär nya bilder" / "Kräv vårdmöte").
 * Doctor only; everyone else gets 403. The images are read with
 * fetchPatientPhotoBlob.
 */
export async function fetchAssessmentPhotos(
  patientId: string,
): Promise<ClinicianAssessmentPhotosView> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/assessment-photos`,
    { headers: commonHeaders(), ...withCredentials },
  );
  return jsonOr401(res);
}

export async function reviewAssessmentPhotos(
  patientId: string,
  body: ReviewAssessmentPhotosInput,
): Promise<ClinicianAssessmentPhotosView> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/assessment-photos/review`,
    {
      method: "POST",
      headers: commonHeaders(),
      body: JSON.stringify(body),
      ...withCredentials,
    },
  );
  return jsonOr401(res);
}

/**
 * TES-1194 (Ö-388 c, Ö-389 b) — the patient's weekly dose schedule and the
 * doctor's two changes to it: "Ändra ordinarie dosdag" and "Ny startdag
 * efter uppehåll". Doctor only; everyone else gets 403.
 */
export async function fetchDoseSchedule(
  patientId: string,
): Promise<DoseScheduleView> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/dose-schedule`,
    { headers: commonHeaders(), ...withCredentials },
  );
  return jsonOr401(res);
}

// TES-1151 S7 (Ö-423) — her dose log. Doctor only; each read is PDL-logged.
export async function fetchDoseLog(patientId: string): Promise<DoseLogView> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/dose-log`,
    { headers: commonHeaders(), ...withCredentials },
  );
  return jsonOr401(res);
}

// TES-1455 (Ö-687) — the doctor corrects her first dose's date after her
// own 24 hours. Versioned and audited; moves nothing in her schedule while
// Ö-789 is open. Answers with the dose log.
export async function correctFirstDose(
  patientId: string,
  doseDate: string,
): Promise<DoseLogView> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/dose-log/first-dose/correction`,
    {
      method: "POST",
      headers: commonHeaders(),
      body: JSON.stringify({ doseDate }),
      ...withCredentials,
    },
  );
  return jsonOr401(res);
}

// TES-1252 — her symptom episodes with every update, safety answers and
// review route (Ö-675: her private registrations included, labelled). Doctor
// only; each read is PDL-logged.
export async function fetchPatientSymptoms(
  patientId: string,
): Promise<StaffPatientSymptomsView> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/symptoms`,
    { headers: commonHeaders(), ...withCredentials },
  );
  return jsonOr401(res);
}

// TES-1252 (Ö-446) — her older-format F-04/F-05 symptom answers, kept apart
// from the episodes. Doctor only.
export async function fetchPatientOlderSymptomRegistrations(
  patientId: string,
): Promise<OlderSymptomRegistrationsView> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/symptoms/older-registrations`,
    { headers: commonHeaders(), ...withCredentials },
  );
  return jsonOr401(res);
}

// TES-1446 (Ö-654, Ö-675) — her "Mående" series, the same read as her own
// "Din hälsa över tid". Doctor only.
export async function fetchPatientWellbeingSeries(
  patientId: string,
): Promise<WellbeingSeriesView> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/health-over-time/wellbeing`,
    { headers: commonHeaders(), ...withCredentials },
  );
  return jsonOr401(res);
}

/**
 * TES-1398 (Ö-420) — an approved prescription transfer's treatment start: the
 * date, exact or estimated, and the treatment day and DT it gives her today.
 * Doctor only; everyone else gets 403.
 */
export async function fetchTransferTreatmentStart(
  patientId: string,
): Promise<TransferTreatmentStartView> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/transfer-treatment-start`,
    { headers: commonHeaders(), ...withCredentials },
  );
  return jsonOr401(res);
}

export async function setTransferTreatmentStart(
  patientId: string,
  body: SetTransferTreatmentStartRequest,
): Promise<TransferTreatmentStartView> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/transfer-treatment-start`,
    {
      method: "PUT",
      headers: commonHeaders(),
      body: JSON.stringify(body),
      ...withCredentials,
    },
  );
  return jsonOr401(res);
}

export async function setDoseScheduleAnchor(
  patientId: string,
  body: SetDoseScheduleAnchorRequest,
): Promise<DoseScheduleView> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/dose-schedule/anchors`,
    {
      method: "POST",
      headers: commonHeaders(),
      body: JSON.stringify(body),
      ...withCredentials,
    },
  );
  return jsonOr401(res);
}

/**
 * TES-1200 (Ö-403) — the medical case's verdict and its recorded final
 * outcomes (doctor + admin read), and the doctor's close / reopen.
 */
export async function fetchMedicalCase(
  patientId: string,
): Promise<MedicalCaseResponse> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/medical-case`,
    { headers: commonHeaders(), ...withCredentials },
  );
  return jsonOr401(res);
}

// TES-1555 — the doctor documents that her care relationship has ended.
export async function endCareRelationship(
  patientId: string,
  body: RecordCareRelationshipEnding,
): Promise<MedicalCaseResponse> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/medical-case/care-relationship-ending`,
    {
      method: "POST",
      headers: commonHeaders(),
      body: JSON.stringify(body),
      ...withCredentials,
    },
  );
  return jsonOr401(res);
}

// A new care process: revokes the standing ending, which stays as history.
export async function revokeCareRelationshipEnding(
  patientId: string,
  endingId: string,
): Promise<MedicalCaseResponse> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/medical-case/care-relationship-ending/${endingId}/revoke`,
    { method: "POST", headers: commonHeaders(), ...withCredentials },
  );
  return jsonOr401(res);
}
